import type { ReportFreshness, ReportMetric } from "./report-metrics";

/**
 * `piracy-audience-countries` v1 — measured countries of the piracy audience
 * report, each with independent IPTV and Web metrics (average and peak viewers,
 * estimated viewers disrupted; coverage on the average). The source can be
 * limited to chosen countries (its Countries filter): the comparison table reads
 * them all, the one-country tile reads its own. Optional `groups` carry metrics
 * computed for explicit platform selections instead of the legacy IPTV/Web fields.
 */
export const PIRACY_AUDIENCE_COUNTRIES_CONTRACT = {
  name: "piracy-audience-countries",
  version: 1,
} as const;

export interface PiracyAudienceCountry {
  /** ISO 3166-1 alpha-2. */
  countryCode: string;
  iptv: ReportMetric[];
  web: ReportMetric[];
  groups?: { id: string; metrics: ReportMetric[] }[];
  /**
   * The average audience over successive equal slices of the period (one a day
   * for a week), oldest first, by platform group id (`iptv`, `web` or a custom
   * group's): only when asked for (the `history` field). `null` marks a slice
   * without measurements, a gap, never zero.
   */
  lines?: Record<string, (number | null)[]>;
}

/**
 * A column of the country tile: a name and the platforms it adds up. Which
 * figures every column shows is a setting of the tile, not of each column
 * (`metricIds`, from earlier tiles, is kept but no longer read).
 */
export interface CountryAudienceGroupConfig {
  id: string;
  label: string;
  platforms: string[];
  metricIds?: string[];
}

export const COUNTRY_AUDIENCE_METRICS = [
  { id: "average-viewers", label: "Average viewers" },
  { id: "max-viewers", label: "Max viewers" },
  { id: "viewers-disrupted", label: "Est. disrupted" },
  { id: "coverage", label: "Coverage" },
] as const;

export const DEFAULT_COUNTRY_AUDIENCE_GROUPS: CountryAudienceGroupConfig[] = [
  { id: "iptv", label: "IPTV", platforms: ["fsp", "m3u"] },
  { id: "web", label: "Web", platforms: ["web"] },
];

export interface PiracyAudienceCountriesData {
  countries: PiracyAudienceCountry[];
  freshness: ReportFreshness;
}

export const PIRACY_AUDIENCE_GROUPS = ["iptv", "web"] as const;
export type PiracyAudienceGroup = (typeof PIRACY_AUDIENCE_GROUPS)[number];

/**
 * One figure of a country and platform group: a metric's value, or `coverage`
 * (the share of the period with measured audience, carried by the average).
 * Missing or non-finite values are `null`, never zero.
 */
export function countryAudienceValue(
  row: PiracyAudienceCountry,
  group: string,
  id: string,
): number | null {
  const metrics =
    row.groups?.find((item) => item.id === group)?.metrics ??
    (group === "iptv" || group === "web" ? row[group] : []);
  const metric = metrics.find((item) => item.id === (id === "coverage" ? "average-viewers" : id));
  const value = id === "coverage" ? metric?.coverage : metric?.value;
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/** A figure of a country and platform group, with its previous-period value when asked for. */
export function countryAudienceMetric(
  row: PiracyAudienceCountry,
  group: string,
  id: string,
): ReportMetric | null {
  const metrics =
    row.groups?.find((item) => item.id === group)?.metrics ??
    (group === "iptv" || group === "web" ? row[group] : []);
  return metrics.find((item) => item.id === id) ?? null;
}

/** The average audience line of a country and platform group, gaps kept; `null` when not asked for. */
export function countryAudienceLine(
  row: PiracyAudienceCountry,
  group: string,
): (number | null)[] | null {
  return row.lines?.[group] ?? null;
}
